import React, { useState, useEffect, useRef } from 'react'

interface LinkDialogProps {
  isOpen: boolean
  onClose: () => void
  onSave: (text: string, url: string) => void
  onRemove?: () => void
  initialText?: string
  initialUrl?: string
  isEditing?: boolean
}

export const LinkDialog: React.FC<LinkDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  onRemove,
  initialText = '',
  initialUrl = '',
  isEditing = false,
}) => {
  const [text, setText] = useState(initialText)
  const [url, setUrl] = useState(initialUrl)
  const textInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setText(initialText)
      setUrl(initialUrl)
      // Focus the text input when dialog opens
      setTimeout(() => {
        textInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen, initialText, initialUrl])

  const handleSave = () => {
    if (url.trim()) {
      onSave(text.trim() || url.trim(), url.trim())
      onClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-96 max-w-[90vw]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-app-grey-25">
          <h3 className="text-lg font-semibold text-app-grey-90">Add link</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-app-grey-20 rounded transition-colors"
            title="Close"
          >
            <span className="text-app-grey-60 text-lg">×</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Text field */}
          <div>
            <label htmlFor="link-text" className="block text-sm font-medium text-app-grey-80 mb-2">
              Text
            </label>
            <input
              ref={textInputRef}
              id="link-text"
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Link text"
              className="w-full px-3 py-2 border border-app-grey-30 rounded-lg focus:outline-none focus:ring-2 focus:ring-app-primary-60 focus:border-transparent"
            />
          </div>

          {/* URL field */}
          <div>
            <label htmlFor="link-url" className="block text-sm font-medium text-app-grey-80 mb-2">
              Link
            </label>
            <input
              id="link-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="https://example.com"
              className="w-full px-3 py-2 border border-app-grey-30 rounded-lg focus:outline-none focus:ring-2 focus:ring-app-primary-60 focus:border-transparent"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-app-grey-25">
          <div>
            {isEditing && onRemove && (
              <button
                onClick={() => {
                  onRemove()
                  onClose()
                }}
                className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                Remove Link
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-app-grey-70 hover:text-app-grey-90 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!url.trim()}
              className="px-4 py-2 bg-app-primary-60 text-white rounded-lg hover:bg-app-primary-70 disabled:bg-app-grey-30 disabled:cursor-not-allowed transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
